<script>
    import dressing from "@intellectability/platform-common";
    import {ACTIONS, SCOPES} from "@intellectability/platform-common/constants";
    import {actionCheck, create_security_error} from "@intellectability/platform-common/utils";
    import {
        AutoUpdateInput,
        ChoosePersonInput,
        Button,
        FlexList,
        Icon,
        Modal,
        toast
    } from "@intellectability/platform-common/sv";
    import {userSecurity} from "@intellectability/platform-common/sv/state";
    import {Col, ModalBody, Row} from "@sveltestrap/sveltestrap";
    import {onMount} from "svelte";
    import {createForm} from "svelte-forms-lib";
    import * as yup from "yup";

    let {id: userID, profile, isActiveProfile} = $props();

    const hasAccess = actionCheck(userSecurity.userActions);
    const userScope = $derived(parseInt(userSecurity.loggedInUser.scope, 10));

    const {USER_TRAINING_NOTES_DELETE, USER_TRAINING_NOTES_EDIT, USER_DETAILS_REQUEST} = ACTIONS;

    let profileIsWithinScope = $derived.by(function () {

        // check state
        if (userScope === SCOPES.STATE) {
            return true;
        }

        const {loggedInUser} = userSecurity;

        // check region
        const isRegionScope = userScope === SCOPES.REGION;
        const profileIsInRegion = profile.region === loggedInUser.region;

        if (isRegionScope) {
            return profileIsInRegion;
        }

        // check provider
        const isProviderScope = userScope === SCOPES.PROVIDER;
        const profileHasSameProvider = profile.employer_id === loggedInUser.employer_id;

        if (isProviderScope) {
            return profileHasSameProvider;
        }

        return false;
    });

    let canDelete = $derived.by(function () {
        // deletion is allowed when
        // 1. The profile is active
        // 2. User has delete permissions
        // 3. The profile is within own scope
        return isActiveProfile && hasAccess(USER_TRAINING_NOTES_DELETE) && profileIsWithinScope;
    });

    // editing is limited it seems to creating a training note
    // after creation
    let canCreateTrainingNote = $derived.by(function () {
        // editing is allowed when
        // 1. The profile is active
        // 2a. User has edit permissions
        // 2b. OR User is a gatekeeper
        // 3. The profile is within own scope
        return isActiveProfile
               && (hasAccess(USER_TRAINING_NOTES_EDIT) || hasAccess(USER_DETAILS_REQUEST))
               && profileIsWithinScope;
    });

    let loading = $state(true);

    /*
            "cons_id": "",
            "created": "2024-01-02 12:08:40",
            "created_by": 2158
            "details": "Coached Cynthia on the importance of clocking in and out on time and by the house phone only. Cynthia stated she came in earlier to assist and forgot to clock in and utilized the attendance request feature instead of using the house phone or calling her Coach to clock her in. I informed her to only use the phone or call her supervisor immediately when she notices that she did not clock in. Also, we went over the correct activity code and job pin when working at the homes. ",
            "end_time": "10:15 AM",
                "id": 1977,
            "ip": "65.43.157.226",
            "person": "Cynthia Addison",
            "start_time": "10:05 AM",
            "subtopic": "Clocking Policy",
            "topic": "Job Description",
            "trainer": "Markeia Witherspoon",
            "training_date": "01/02/2024",
            "user_id": 12,
     */

    /**
     * @typedef TrainingNote
     * @property {string} cons_id
     * @property {string} created - a date time string, no formatting needed
     * @property {number} created_by
     * @property {string} details
     * @property {string} end_time - example `10:15 AM`
     * @property {string} ip
     * @property {string} person - first and last name
     * @property {string} start_time - example `10:05 AM`
     * @property {string} subtopic
     * @property {string} topic
     * @property {string} trainer - first and last name
     * @property {string} training_date - a date string formatted as `mm/dd/yyyy`
     * @property {number} user_id
     *
     */

    /**
     * @type {TrainingNote[]}
     */
    let trainingNotes = $state.raw([]);

    /**
     * @typedef Topic
     * @property {string} topic
     * @property {string[]} subtopics
     *
     * @example
     * ```JSON
     *  {
     *     "topic": "ASP Training",
     *     "subtopics": [
     *       "Important to Sort",
     *       "Support Instructions - Choice and Control",
     *       "Support Instructions - Health",
     *       "Support Instructions - Hygiene"
     *     ]
     *   }
     */

    /**
     * @type {Topic[]}
     */
    let trainingTopics = $state.raw([]);

    /**
     * @type {string[]}
     */
    let topics = $derived(trainingTopics.map(e => e.topic));

    // pagination
    let pagination = $state({
        show: true,
        page: 1,
        limit: 10,
        pageLengthMenu: [10, 25, 50, 100, 500]
    });

    // sort
    let sort = $state({
        field: "training_date",
        order: "asc"
    });

    let columns = $derived([
        {
            collapse: true,
            name: "Date",
            field: "training_date",
            sortedBy: sort.field === "training_date" ? sort.order : "",
            sortable: true
        },
        {
            collapse: false,
            name: "Start Time",
            field: "start_time",
            sortedBy: sort.field === "start_time" ? sort.order : "",
            sortable: true
        },
        {
            collapse: true,
            name: "End Time",
            field: "end_time",
            sortedBy: sort.field === "end_time" ? sort.order : "",
            sortable: true
        },
        {
            collapse: false,
            name: "Topic",
            field: "topic",
            sortedBy: sort.field === "topic" ? sort.order : "",
            sortable: true
        },
        {
            collapse: true,
            name: "Trainer",
            field: "trainer",
            sortedBy: sort.field === "trainer" ? sort.order : "",
            sortable: true
        },
        {
            collapse: true,
            name: "Person",
            field: "person",
            sortedBy: sort.field === "person" ? sort.order : "",
            sortable: true
        },
        {
            name: "Actions",
            width: "110px",
            buttons: [
                {
                    icon: "times-circle",
                    tooltip: "Delete",
                    variant: "danger",
                    action: function (_, row) {
                        const {id} = row;
                        if (canDelete === true && id) {
                            dressing.post(`/users/training_notes/delete/${id}`).then(
                                /** @param {{ data: {success:boolean} }} data */({data: res}) => {
                                    const {success} = res;

                                    if (success === true) {
                                        filter_list();
                                    }
                                    else {
                                        toast.error("Could not delete training note.");
                                    }
                                }
                            ).catch(() => toast.error("Could not delete training note."));
                        }
                    },
                    disabled() {
                        return canDelete === false;
                    }
                }
            ]
        }
    ]);

    let showCreateNoteModal = $state(false);

    let commonInputProps = $derived({
        autoupdate: false,
        loading
    });

    const validationSchema = yup.object().shape({
        cons_id: yup.number().nullable(),
        training_date: yup.date().required(),
        details: yup.string().required(),
        end_time: yup.string().required("When did the training end?")
        .test(
            "is-greater-than-start",
            "End time must be greater than start time",
            function isEndTimeGreaterThanStartTime(end_time) {

                const {start_time} = this.parent;

                if (!start_time || !end_time) {
                    return true; // Let required() handle empty values
                }

                const parseTime = (timeStr) => {
                    if (!timeStr) {
                        return null;
                    }

                    // Handle both 24-hour format (HH:MM) and 12-hour format (HH:MM AM/PM)
                    let [time, period] = timeStr.split(" ");
                    let [hours, minutes] = time.split(":").map(Number);

                    // Convert to 24-hour format if needed
                    if (period) {
                        if (period.toUpperCase() === "PM" && hours !== 12) {
                            hours += 12;
                        }
                        else if (period.toUpperCase() === "AM" && hours === 12) {
                            hours = 0;
                        }
                    }

                    return hours * 60 + minutes;
                };

                const startMinutes = parseTime(start_time);
                const endMinutes = parseTime(end_time);

                return endMinutes > startMinutes;
            }
        ),
        start_time: yup.string().required("When did the training start?"),
        subtopic: yup.string(),
        topic: yup.string().required(),
        trainer: yup.string().required()
    });

    const maxTrainingDate = new Date().toISOString().split("T")[0]; // prevent selecting future dates

    const blank_training_note = {
        cons_id: null,
        training_date: "",
        details: "",
        end_time: "",
        person: "",
        start_time: "",
        subtopic: "",
        topic: "",
        trainer: "",
        user_id: userID
    };

    /*
    {
            cons_id: null,
            training_date: "",
            start_time: "",
            end_time: "",
            trainer: "",
            person: "",
            topic: "",
            subtopic: "",
            details: "",
            user_id: null
        },
     */

    const {form, errors, handleSubmit, updateInitialValues} = createForm({
        initialValues: {
            cons_id: 76,
            training_date: "2025-01-01",
            start_time: "14:49",
            end_time: "14:50",
            trainer: "Sarah",
            topic: "ASP Training",
            subtopic: "Support Instructions - Choice and Control",
            details: "What Sarah Said",
            user_id: userID
        },
        validationSchema,
        onSubmit: (values) => {

            if (!canCreateTrainingNote) {
                dressing.api.report_security_error(
                    create_security_error("UserTrainingNotes.svelte", "UserProfile", USER_TRAINING_NOTES_EDIT))
                .then(() => dressing.api.logout());
                return;
            }

            loading = true;
            dressing.post("/users/training_notes/create", values)
            .then(({data: res}) => {
                if (res.success === true) {
                    showCreateNoteModal = false;
                    filter_list();
                    toast.success("Training note created successfully.");
                }
                else {
                    toast.error("Failed to create training note.");
                }
            })
            .catch(() => {
                toast.error("Failed to create training note.");
            })
            .finally(() => loading = false);

        }
    });

    /** @type {string} */
    let selectedTopic = $derived($form.topic);
    /** @type {string[]} */
    let availableSubtopics = $derived(trainingTopics.find(e => e.topic === selectedTopic)?.subtopics || []);

    onMount(function onTrainingNotesMount() {
        dressing.get("/users/training_note_topics")
        .then(({data: res}) => {

            const {success, data} = res;

            if (success === true) {
                // since I'm getting the whole table, I need to break it down into something useful
                const the_whole_table = data.topics;

                /** @type {object} */
                const topicData = the_whole_table.reduce((acc, row) => {
                    const {topic, subtopic, sort_order} = row;

                    if (!acc[topic]) {
                        acc[topic] = {
                            subtopics: [],
                            needsSorting: false
                        };
                    }

                    // Check if this topic needs sorting
                    if (sort_order) {
                        acc[topic].needsSorting = true;
                    }

                    // Add subtopic if it exists
                    if (subtopic) {
                        acc[topic].subtopics.push(row);
                    }

                    return acc;
                }, {});

                // Now build the final structure efficiently

                /**
                 * @type {Topic[]}
                 */
                let training_topics = Object.keys(topicData).map(topic => {
                    let filtered = topicData[topic].subtopics;

                    // Sort if needed
                    if (topicData[topic].needsSorting) {
                        filtered = filtered.sort((a, b) => a.sort_order - b.sort_order);
                    }

                    return {
                        topic,
                        subtopics: filtered.map(e => e.subtopic)
                    };
                });

                if (training_topics.length > 0) {
                    trainingTopics = training_topics;
                }
            }
        });
    });

    $effect(filter_list);

    function filter_list() {

        if (profileIsWithinScope) {
            const sort_query = {
                sort: sort.field || null,
                order: sort.order || null
            };

            const page_query = {
                page: pagination.page || null,
                limit: pagination.limit || null
            };

            dressing.get(`/users/training_notes/${userID}/`, {
                params: Object.assign({...sort_query, ...page_query})
            }).then(({data: res}) => {
                const {success, data} = res;
                if (success === true) {
                    const {notes: t} = data;

                    if (Array.isArray(t)) {
                        trainingNotes = t;
                        pagination.page = data.page || 1;
                        pagination.limit = data.limit || 10;
                        pagination.recordsTotal = data.recordsTotal || 0;
                    }
                }
            }).finally(() => loading = false);
        }
    }

</script>

<div class="mb-3">
    <h2>
        <Icon icon="chalkboard-teacher" class="me-1"/>
        Training Notes
    </h2>
</div>

<FlexList
        loading={loading}
        pagerConfig={pagination}
        onpagechange={(page) => pagination.page = page}
        onsort={(field, order) => {sort = {field, order};}}
        onpagelengthchange={(newLimit) => pagination.limit = newLimit}
        {columns}
        data={trainingNotes}
        expandableRows
        expandableRowsComponent={expansionSnippet}
>
</FlexList>


{#if canCreateTrainingNote === true}
    <Button size="small" onclick={() => showCreateNoteModal = true}>New Training Note</Button>
    <Modal bind:showModal={showCreateNoteModal} size="xl" title="Create New Training Note">
        <ModalBody>
            <form onsubmit={handleSubmit} novalidate>
                <input type="hidden" name="user_id" value={$form.user_id}/>
                <fieldset disabled={loading}>
                    <Row class="mb-3">
                        <Col xs="12" lg="4">
                            <div class="mt-3">
                                <AutoUpdateInput autofocus
                                                 max={maxTrainingDate}
                                                 bind:defaultValue={$form.training_date}
                                                 fieldName="training_date"
                                                 label="Training Date"
                                                 invalid={$errors.training_date.length > 0}
                                                 invalidMessage="Please enter the training date"
                                                 type="date"
                                                 {...commonInputProps}
                                />
                            </div>
                        </Col>
                        <Col xs="12" lg="4">
                            <div class="mt-3">
                                <AutoUpdateInput
                                        bind:defaultValue={$form.start_time}
                                        fieldName="start_time"
                                        label="Start Time"
                                        invalid={$errors.start_time.length > 0}
                                        invalidMessage={$errors.start_time}
                                        type="time"
                                        {...commonInputProps}
                                />
                            </div>
                        </Col>
                        <Col xs="12" lg="4">
                            <div class="mt-3">
                                <AutoUpdateInput
                                        bind:defaultValue={$form.end_time}
                                        fieldName="end_time"
                                        label="End Time"
                                        invalid={$errors.end_time.length > 0}
                                        invalidMessage={$errors.end_time}
                                        type="time"
                                        {...commonInputProps}
                                />
                            </div>
                        </Col>
                    </Row>
                    <Row class="mb-3">
                        <Col xs="12" md="6">
                            <div class="mt-3">
                                <AutoUpdateInput
                                        bind:defaultValue={$form.trainer}
                                        fieldName="trainer"
                                        label="Trainer"
                                        invalid={$errors.trainer.length > 0}
                                        invalidMessage="Who did the training?"
                                        type="string"
                                        {...commonInputProps}
                                />
                            </div>
                        </Col>
                        <Col xs="12" md="6">
                            <div class="mt-3">
                                <ChoosePersonInput
                                        context="user_training_notes"
                                        onselected={function(data) {
                                            $form.cons_id = parseInt(data.value, 10) || null;
                                        }}/>
                            </div>
                        </Col>
                    </Row>
                    <Row class="mb-3">
                        <Col xs="12" md="6">
                            <div class="mt-3">
                                <AutoUpdateInput
                                        bind:defaultValue={$form.topic}
                                        fieldName="topic"
                                        label="Training Topic"
                                        invalid={$errors.topic.length > 0}
                                        invalidMessage="What was the the training on?"
                                        type="select"
                                        optionsFrom={topics}
                                        onupdate={function() {
                                        $form.subtopic = "";
                                    }}
                                        {...commonInputProps}
                                />
                            </div>
                        </Col>
                        <Col xs="12" md="6">
                            <div class="mt-3">
                                <AutoUpdateInput
                                        disabled={availableSubtopics.length === 0}
                                        bind:defaultValue={$form.subtopic}
                                        fieldName="subtopic"
                                        label="Training Subtopics"
                                        type="select"
                                        optionsFrom={availableSubtopics}
                                        {...commonInputProps}
                                />
                            </div>
                        </Col>
                    </Row>
                    <Row class="mb-3">
                        <Col>
                            <div class="mt-3">
                                <AutoUpdateInput
                                        bind:defaultValue={$form.details}
                                        fieldName="details"
                                        invalid={$errors.details.length > 0}
                                        invalidMessage="Please add training details"
                                        label="Details"
                                        type="textarea"
                                        {...commonInputProps}
                                />
                            </div>
                        </Col>
                    </Row>
                    <div class="d-flex justify-content-between">
                        <Button variant="neutral" type="button"
                                onclick={function () {
                                    updateInitialValues(blank_training_note);
                                    showCreateNoteModal = false;
                                }}
                        >Cancel
                        </Button>
                        <Button variant="brand" type="submit">
                            Save Training Note
                        </Button>
                    </div>
                </fieldset>
            </form>

        </ModalBody>
    </Modal>

{/if}

{#snippet expansionSnippet(/** @type {TrainingNote} */ notification)}
    <wa-callout appearance="plain" variant="neutral" class="rounded-0">
        <Row>
            <Col class="col-12 col-md-6 col-lg-4">
                <div class="d-flex">
                    <div class="fw-bold me-3">
                        Date:
                    </div>
                    <div>
                        {notification.training_date}
                    </div>
                </div>
            </Col>
            <Col class="col-12 col-md-6 col-lg-4">
                <div class="d-flex">
                    <div class="fw-bold me-3">
                        Start Time:
                    </div>
                    <div>
                        {notification.start_time}
                    </div>
                </div>
            </Col>
            <Col class="col-12 col-md-6 col-lg-4">
                <div class="d-flex">
                    <div class="fw-bold me-3">
                        End Time:
                    </div>
                    <div>
                        {notification.end_time}
                    </div>
                </div>
            </Col>
        </Row>
        <wa-divider></wa-divider>
        <div>
            <div class="d-flex">
                <div class="fw-bold me-3">
                    Topic:
                </div>
                <div>
                    {notification.topic}
                </div>
            </div>
        </div>
        <div>
            <div class="d-flex">
                <div class="fw-bold me-3">
                    Subtopic:
                </div>
                <div>
                    {notification.subtopic}
                </div>
            </div>
        </div>
        <wa-divider></wa-divider>
        <Row>
            <Col class="col-12 col-md-6">
                <div class="d-flex">
                    <div class="fw-bold me-3">
                        Trainer:
                    </div>
                    <div>
                        {notification.trainer}
                    </div>
                </div>
            </Col>
            <Col class="col-12 col-md-6">
                <div class="d-flex">
                    <div class="fw-bold me-3">
                        Person:
                    </div>
                    <div>
                        {notification.person}
                    </div>
                </div>
            </Col>
        </Row>
        <wa-divider></wa-divider>
        <div class="fw-bold">
            Note:
        </div>
        <div>
            {notification.details}
        </div>
    </wa-callout>
{/snippet}